import React, { useEffect, useRef } from 'react';
import type {
  CandidateLegalTechSkill,
  GroupedExperiences,
  Taxonomy,
  BioRenderableCandidate,
} from '@axiom/validation';

import { StringUtil } from '../../../utils/string-util';
import type { PDFExportRef } from '../../layout/PDFExport/PDFExport';
import { PDFExport } from '../../layout/PDFExport/PDFExport';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { Layout } from '../../layout/Layout/Layout';
import { Gutter } from '../../layout/Gutter/Gutter';
import { PdfPageTemplateFooter } from '../PdfPageTemplateFooter/PdfPageTemplateFooter';

import { BioPdfFooter } from './BioPdfFooter';
import { BioPdfCandidate } from './BioPdfCandidate';

export type BioPdfProps = {
  name?: string;
  isDownloading?: boolean;
  candidate: BioRenderableCandidate;
  groupedExperiences: GroupedExperiences[];
  isAnonymous?: boolean;
  clientUrl: string;
  hideAllHighlights?: boolean;
  hideHighlightsDetails?: boolean;
  hideLocation?: boolean;
  onSave?: () => void;
  taxonomy: Taxonomy;
  legalTechSkills: CandidateLegalTechSkill[];
  isAhqBio?: boolean;
};

export const BioPdf = ({
  name = 'HIDE_RENDERING',
  isDownloading,
  candidate,
  groupedExperiences,
  isAnonymous = false,
  clientUrl,
  hideAllHighlights,
  hideHighlightsDetails = false,
  hideLocation,
  onSave,
  taxonomy,
  legalTechSkills,
  isAhqBio = false,
}: BioPdfProps) => {
  const pdfRoot = useRef<PDFExportRef>(null);

  const renderPageTemplate = (props: {
    pageNum: number;
    totalPages: number;
  }) => {
    return <PdfPageTemplateFooter pdfProps={props} candidate={candidate} />;
  };

  useEffect(() => {
    const listenerFunction = window.close;
    if (pdfRoot.current?.save) {
      if (typeof onSave === 'function') {
        setTimeout(() => pdfRoot.current?.save(onSave), 500);
      } else {
        window.addEventListener('focus', listenerFunction);

        setTimeout(() => pdfRoot.current?.save(), 500);
      }
    }

    return () => {
      window.removeEventListener('focus', listenerFunction);
    };
  }, [pdfRoot]);

  return (
    <div data-test={name}>
      <PDFExport
        pageTemplate={props => renderPageTemplate(props)}
        fileName={`${StringUtil.cleanStringForFileName(
          candidate.calculatedDisplayName
        )} - Axiom`}
        ref={pdfRoot}
        scale={0.5}
        margin={{ top: '0in', right: '0in', bottom: '0in', left: '0in' }}
      >
        <Layout name="BIO_PDF" background="panelLight">
          <LayoutItem fluid>
            <Gutter horizontal="40px" vertical="24px">
              <BioPdfCandidate
                isAhqBio={isAhqBio}
                isDownloading={isDownloading}
                hideAllHighlights={hideAllHighlights}
                hideHighlightsDetails={hideHighlightsDetails}
                hideLocation={hideLocation}
                candidate={candidate}
                groupedExperiences={groupedExperiences}
                isAnonymous={isAnonymous}
                removeTalentImage
                taxonomy={taxonomy}
                legalTechSkills={legalTechSkills}
              />
              <BioPdfFooter clientUrl={clientUrl} />
            </Gutter>
          </LayoutItem>
        </Layout>
      </PDFExport>
    </div>
  );
};
